<template>
  <div class="home-view">
    <div id="indicators" class="indicators-view">
      <div class="tab-content-wrapper">
          <div class="indicators-grid">
              <!-- Columna izquierda: Notas + Total de Configuraciones (como en diseño) -->
              <div class="indicators-left">
                <h1 class="indicators-title">Indicadores del Distribudor</h1>

                <v-card class="summary-card" elevation="2" outlined>
                  <v-card-title class="py-2 px-3 text-subtitle-1">
                    <v-icon left small color="grey darken-1">mdi-chart-box-outline</v-icon>
                    Totales Generales
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="pa-3">
                    <div class="totals-stack">
                      <div class="total-item total-item--compact">
                        <div class="total-item__icon enterprises"><v-icon>mdi-office-building</v-icon></div>
                        <div class="total-item__content">
                          <span class="total-item__value">{{ formatNumber(enterprisesTotal) }}</span>
                          <span class="total-item__label">Empresas Totales</span>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Columna derecha: Filtros + Gráfico -->
              <div class="indicators-right">
                <v-card elevation="2" class="chart-card">
                  <v-card-title class="py-2 px-3 text-subtitle-1 d-flex align-center flex-wrap">
                    <v-icon left small>mdi-office-building-marker</v-icon>
                    Empresas por período
                    <v-spacer />
                    <div class="filters-inline">
                      <v-select
                        v-model="filterMode"
                        :items="filterModeOptions"
                        label="Filtrar"
                        outlined
                        dense
                        hide-details
                        class="filter-select"
                        @change="onFilterModeChange"
                      />
                      <v-select
                        v-if="filterMode === 'days'"
                        v-model="days"
                        :items="daysOptions"
                        label="Período"
                        outlined
                        dense
                        hide-details
                        class="filter-select"
                        @change="fetchData"
                      />
                      <template v-else-if="filterMode === 'month'">
                        <v-select
                          v-model="selectedMonth"
                          :items="monthOptions"
                          label="Mes"
                          outlined
                          dense
                          hide-details
                          class="filter-select"
                          @change="fetchData"
                        />
                        <v-select
                          v-model="selectedYear"
                          :items="yearOptions"
                          label="Año"
                          outlined
                          dense
                          hide-details
                          class="filter-select"
                          @change="fetchData"
                        />
                      </template>
                      <v-select
                        v-else-if="filterMode === 'year'"
                        v-model="selectedYear"
                        :items="yearOptions"
                        label="Año"
                        outlined
                        dense
                        hide-details
                        class="filter-select"
                        @change="fetchData"
                      />
                      <v-btn
                        depressed
                        :loading="loading"
                        :disabled="loading"
                        @click="fetchData"
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        Actualizar
                      </v-btn>
                      <v-btn
                        depressed
                        small
                        :disabled="loading || filteredEnterprises.length === 0"
                        @click="downloadCSV"
                      >
                        <v-icon left small>mdi-file-download</v-icon>
                        CSV
                      </v-btn>
                    </div>
                  </v-card-title>
                  <v-divider />

                  <v-progress-linear
                    v-if="loading"
                    indeterminate
                    :color="themeColor"
                    dark
                    height="2"
                  />

                  <v-card-text class="chart-content">
                    <template v-if="!loading && !error">
                      <div class="enterprises-summary">
                        <span class="enterprises-summary__number">{{ formatNumber(totalEnterprisesInPeriod) }}</span>
                        <span class="enterprises-summary__label">{{ filterPeriodLabel }}</span>
                      </div>
                      <div v-if="chartSeries.length > 0" class="chart-wrapper">
                        <div class="chart-inner">
                          <apexchart
                            type="bar"
                            :height="chartHeight"
                            :options="chartOptions"
                            :series="chartSeries"
                          />
                        </div>
                      </div>
                      <v-alert v-else type="info" outlined dense class="mt-2">
                        No hay datos para el período seleccionado.
                      </v-alert>
                    </template>
                  </v-card-text>
                </v-card>
              </div>
          </div>
    </div>

      <v-alert v-if="error" type="error" dense class="ma-2" dismissible>
        {{ error }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { mapGetters } from 'vuex';
import moment from 'moment';

const MONTHS = [
  { value: 1, text: 'Enero' }, { value: 2, text: 'Febrero' }, { value: 3, text: 'Marzo' },
  { value: 4, text: 'Abril' }, { value: 5, text: 'Mayo' }, { value: 6, text: 'Junio' },
  { value: 7, text: 'Julio' }, { value: 8, text: 'Agosto' }, { value: 9, text: 'Septiembre' },
  { value: 10, text: 'Octubre' }, { value: 11, text: 'Noviembre' }, { value: 12, text: 'Diciembre' },
];

export default {
  middleware: 'auth',
  components: {
    apexchart: VueApexCharts,
  },
  metaInfo() {
    return { title: this.$t('indicators') };
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: true,
      error: null,
      filterMode: 'days',
      filterModeOptions: [
        { value: 'days', text: 'Por días' },
        { value: 'month', text: 'Por mes' },
        { value: 'year', text: 'Por año' },
      ],
      days: 31,
      daysOptions: [
        { value: 7, text: 'Últimos 7 días' },
        { value: 31, text: 'Últimos 31 días' },
        { value: 90, text: 'Últimos 90 días' },
        { value: 365, text: 'Últimos 365 días' },
      ],
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: currentYear,
      monthOptions: MONTHS,
      enterprisesTotal: 0,
      enterprisesPerDay: [],
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 600,
    };
  },
  computed: {
    ...mapGetters({
      themeColor: 'theme/themeColor',
    }),
    chartHeight() {
      const vh = this.windowHeight;
      const available = Math.max(280, vh - 320);
      return Math.min(available, 500);
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, i) => ({
        value: currentYear - i,
        text: String(currentYear - i),
      }));
    },
    totalEnterprisesInPeriod() {
      return this.filteredEnterprises.reduce((sum, item) => sum + item.count, 0);
    },
    filteredEnterprises() {
      if (this.filterMode === 'days') return this.enterprisesPerDay;

      const filtered = this.enterprisesPerDay.filter((item) => {
        const date = moment(item.date);
        if (this.filterMode === 'month') {
          return date.month() + 1 === this.selectedMonth && date.year() === this.selectedYear;
        }
        if (this.filterMode === 'year') {
          return date.year() === this.selectedYear;
        }
        return true;
      });

      if (this.filterMode === 'year' && filtered.length > 31) {
        const byMonth = {};
        filtered.forEach((item) => {
          const date = moment(item.date);
          const key = date.format('YYYY-MM');
          if (!byMonth[key]) {
            byMonth[key] = { date: date.startOf('month').format('YYYY-MM-DD'), count: 0 };
          }
          byMonth[key].count += item.count;
        });
        return Object.values(byMonth).sort((a, b) => a.date.localeCompare(b.date));
      }
      return filtered;
    },
    filterPeriodLabel() {
      if (this.filterMode === 'days') {
        return `Empresas registradas (últimos ${this.days} días)`;
      }
      if (this.filterMode === 'month') {
        const monthName = MONTHS.find((m) => m.value === this.selectedMonth)?.text || '';
        return `Empresas registradas en ${monthName} ${this.selectedYear}`;
      }
      if (this.filterMode === 'year') {
        return `Empresas registradas en ${this.selectedYear}`;
      }
      return 'Empresas registradas';
    },
    chartOptions() {
      const useMonthFormat = this.filterMode === 'year' && this.filteredEnterprises.length <= 12;
      const categories = this.filteredEnterprises.map((item) => moment(item.date).format(useMonthFormat ? 'MMM YYYY' : 'DD/MM'));
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: this.filteredEnterprises.length > 30 ? '80%' : '65%',
            borderRadius: 6,
            distributed: false,
            dataLabels: { position: 'top' },
          },
        },
        colors: ['#0083CB'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.4,
            gradientToColors: ['#00a8e8'],
            opacityFrom: 0.9,
            opacityTo: 0.7,
          },
        },
        dataLabels: {
          enabled: this.filteredEnterprises.length <= 31,
          formatter: (val) => (val > 0 ? val : ''),
          offsetY: -20,
          style: { fontSize: '11px' },
        },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        xaxis: {
          categories,
          labels: {
            rotate: this.filteredEnterprises.length > 14 ? -45 : 0,
            rotateAlways: this.filteredEnterprises.length > 14,
            style: { fontSize: '11px' },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        yaxis: {
          title: { text: 'Recuento de empresas' },
          labels: { style: { fontSize: '12px' } },
          axisBorder: { show: false },
          tickAmount: 5,
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        tooltip: {
          theme: 'light',
          x: {
            formatter: (val) => {
              const item = this.filteredEnterprises[val];
              return item ? moment(item.date).format('dddd, DD [de] MMMM YYYY') : val;
            },
          },
          y: {
            formatter: (val) => `${val} empresa${val !== 1 ? 's' : ''}`,
          },
        },
        legend: { show: false },
      };
    },
    chartSeries() {
      if (this.filteredEnterprises.length === 0) return [];
      return [{
        name: 'Empresas',
        data: this.filteredEnterprises.map((item) => item.count),
      }];
    },
  },
  mounted() {
    this.fetchData();
    this.updateWindowHeight();
    window.addEventListener('resize', this.updateWindowHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowHeight);
  },
  methods: {
    updateWindowHeight() {
      this.windowHeight = window.innerHeight || 600;
    },
    onFilterModeChange() {
      this.selectedMonth = new Date().getMonth() + 1;
      this.selectedYear = new Date().getFullYear();
      this.fetchData();
    },
    getRequestParams() {
      if (this.filterMode === 'days') {
        return { days: this.days };
      }
      if (this.filterMode === 'month') {
        const now = moment();
        const selected = moment({ year: this.selectedYear, month: this.selectedMonth - 1 });
        if (selected.isAfter(now, 'month')) return { days: 31 };
        return { year: this.selectedYear, month: this.selectedMonth };
      }
      if (this.filterMode === 'year') {
        const now = moment();
        const yearStart = moment({ year: this.selectedYear, month: 0, day: 1 });
        if (yearStart.isAfter(now)) return { days: 31 };
        return { year: this.selectedYear };
      }
      return { days: 31 };
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const params = this.getRequestParams();
        const { data } = await window.axios.get('/api/indicators/dashboard-dealers', { params });
        this.enterprisesTotal = data.enterprises_total ?? 0;
        const rep = data.enterprises_by_representation || {};
        this.enterprisesByRep = {
          fel: rep.fel ?? 0,
          pac: rep.pac ?? 0,
          felpac: rep.felpac ?? 0,
          recepcion: rep.recepcion ?? 0,
          notActivatedFELPACRECEP: rep.not_activated_felpacrecep ?? 0,
          notActivatedTotal: rep.not_activated_total ?? 0,
        };
        this.enterprisesPerDay = data.enterprises_per_day?.data ?? [];
      } catch (err) {
        this.error = err.response?.data?.message
          || 'Error al cargar los indicadores. Verifica que la ruta /api/indicators/dashboard exista.';
        this.$store.dispatch('notifications/show', {
          message: this.error,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-PA').format(value);
    },
    downloadCSV() {
      if (this.filteredEnterprises.length === 0) {
        this.$store.dispatch('notifications/show', {
          message: 'No hay datos para exportar',
          type: 'warning',
        });
        return;
      }
      const headers = ['Fecha', 'Empresas registradas'];
      const rows = this.filteredEnterprises.map((item) => [
        moment(item.date).format('YYYY-MM-DD'),
        item.count,
      ]);
      const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `empresas_registradas_${moment().format('YYYY-MM-DD')}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      this.$store.dispatch('notifications/show', {
        message: 'CSV descargado correctamente',
        type: 'success',
      });
    },
  },
};
</script>


<style scoped lang="scss">
.indicators-view {
  height: calc(100vh - 120px);
  min-height: 480px;
}

.indicators-view .v-tabs {
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.indicators-view .v-tab-item {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.tab-content-wrapper {
  height: calc(100vh - 180px);
  min-height: 420px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.indicators-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 16px;
  padding: 12px;
  min-height: 0;
}

.indicators-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  overflow-y: auto;
  overflow-x: visible;
}

.indicators-right {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.indicators-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1976d2;
  margin: 0 0 4px 0;
  flex-shrink: 0;
}

.summary-card {
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
}

.totals-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.total-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-1px);
  }

  &--compact .total-item__icon {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }

  &--compact .total-item__value {
    font-size: 1.25rem;
  }

  &--compact .total-item__label {
    font-size: 0.7rem;
  }

  &__icon {
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.enterprises {
      background: linear-gradient(135deg, #0984e3 0%, #74b9ff 100%);
      color: white;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__label {
    font-size: 0.75rem;
    color: #212529 !important;
    white-space: nowrap;
  }

  &__value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #212529;
  }
}

.chart-card {
  border-radius: 10px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.filters-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-select {
  max-width: 140px;
}

.chart-content {
  flex: 1;
  padding: 12px 16px !important;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.enterprises-summary {
  padding: 12px 16px;
  background: linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%);
  border-radius: 8px;
  border-left: 4px solid #00897b;
  margin-bottom: 12px;
  flex-shrink: 0;

  &__number {
    display: inline;
    font-size: 1.75rem;
    font-weight: 700;
    color: #00695c;
    margin-right: 6px;
  }

  &__label {
    font-size: 0.8rem;
    color: #212529 !important;
  }
}

.chart-wrapper {
  flex: 1;
  min-height: 280px;
  border-radius: 8px;
  overflow: auto;
  display: flex;
}

.chart-inner {
  flex: 1;
  min-width: 100%;
  min-height: 280px;
  padding-right: 24px;
  box-sizing: border-box;
}

/* Forzar color oscuro en labels - override Vuetify */
.indicators-view .total-item__label,
.indicators-view .rep-item__label,
.indicators-view .enterprises-summary__label,
.indicators-view .legend-title,
.indicators-view .legend-text {
  color: #212529 !important;
}

.home-view {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

@media (max-width: 960px) {
  .indicators-grid {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .indicators-view {
    height: auto;
    min-height: auto;
    overflow-y: auto;
  }
}
</style>
